import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { errorText, get, setOrganisation } from 'src/common/api';
import { useAuth } from 'src/context/AuthContext';

// Shared state for every page: the organisation (?workspace=), the chosen
// business (?location=, or "all"), the insights range, the Google connection
// snapshot and the catalog of options from the backend.
const WorkspaceContext = createContext(null);
const RANGE_KEY = 'autoappy.gmb.range';

export const RANGES = [
  { value: 'last_7d', label: 'Last 7 days' },
  { value: 'last_28d', label: 'Last 28 days' },
  { value: 'last_90d', label: 'Last 3 months' },
  { value: 'last_6m', label: 'Last 6 months' },
  { value: 'last_12m', label: 'Last 12 months' },
];

const readRange = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(RANGE_KEY));
    if (stored?.preset) return stored;
  } catch { /* ignore */ }
  return { preset: 'last_28d' };
};

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const organisations = useMemo(() => user?.workspaces || [], [user]);
  const requested = params.get('workspace');
  const organisationId = useMemo(() => {
    if (requested && organisations.some((o) => o.id === requested)) return requested;
    const current = user?.current_workspace?.id;
    if (current && organisations.some((o) => o.id === current)) return current;
    return organisations[0]?.id || '';
  }, [requested, organisations, user]);
  setOrganisation(organisationId);

  const [boot, setBoot] = useState(null);
  const [bootError, setBootError] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [range, setRangeState] = useState(readRange);
  const loadedFor = useRef('');

  const refresh = useCallback(async () => {
    if (!organisationId) return null;
    try {
      const response = await get('/api/bootstrap');
      setBoot(response.data);
      setBootError(null);
      return response.data;
    } catch (error) {
      setBootError(errorText(error));
      return null;
    }
  }, [organisationId]);

  useEffect(() => {
    if (!organisationId || loadedFor.current === organisationId) return;
    loadedFor.current = organisationId;
    setBoot(null);
    refresh();
  }, [organisationId, refresh]);

  useEffect(() => {
    if (catalog || !organisationId) return;
    get('/api/catalog').then((response) => setCatalog(response.data)).catch(() => {});
  }, [catalog, organisationId]);

  // Keep ?workspace= in the address so links can be shared.
  useEffect(() => {
    if (organisationId && params.get('workspace') !== organisationId) {
      const next = new URLSearchParams(params);
      next.set('workspace', organisationId);
      setParams(next, { replace: true });
    }
  }, [organisationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const allLocations = useMemo(() => boot?.google?.locations || [], [boot]);
  const locations = useMemo(() => allLocations.filter((l) => l.enabled), [allLocations]);
  const requestedLocation = params.get('location');
  /** The business a page shows; null means "All businesses" (when there are several). */
  const location_ = useMemo(() => {
    if (requestedLocation === 'all' && locations.length > 1) return null;
    return locations.find((l) => l.id === requestedLocation) || locations.find((l) => l.is_default) || locations[0] || null;
  }, [locations, requestedLocation]);
  /** Always one business, for pages that need exactly one (profile, SEO). */
  const single = location_ || locations.find((l) => l.is_default) || locations[0] || null;

  const chooseLocation = useCallback((id) => {
    const next = new URLSearchParams(params);
    next.set('location', id);
    ['focus', 'page'].forEach((key) => next.delete(key));
    setParams(next);
  }, [params, setParams]);

  const chooseOrganisation = useCallback((id) => {
    const next = new URLSearchParams();
    next.set('workspace', id);
    loadedFor.current = '';
    const base = /^\/posts\/[^/]+/.test(location.pathname) ? '/posts' : location.pathname;
    navigate(`${base}?${next}`);
  }, [location.pathname, navigate]);

  const setRange = useCallback((value) => {
    setRangeState(value);
    try { localStorage.setItem(RANGE_KEY, JSON.stringify(value)); } catch { /* ignore */ }
  }, []);

  const organisation = organisations.find((o) => o.id === organisationId) || null;
  const canManage = ['owner', 'admin'].includes(organisation?.role);

  /** Add the organisation (and chosen business) to an in-app link. */
  const link = useCallback((path, extra = {}) => {
    const [withoutHash, hash = ''] = path.split('#');
    const [base, query = ''] = withoutHash.split('?');
    const next = new URLSearchParams(query);
    if (organisationId) next.set('workspace', organisationId);
    if (requestedLocation && !next.has('location') && extra.location !== false) next.set('location', requestedLocation);
    Object.entries(extra).forEach(([key, value]) => { if (key !== 'location' && value !== undefined && value !== null && value !== false) next.set(key, value); });
    return `${base}?${next}${hash ? `#${hash}` : ''}`;
  }, [organisationId, requestedLocation]);

  const google = boot?.google || {};
  const value = {
    user, organisations, organisation, organisationId, chooseOrganisation, canManage,
    boot, bootError, refresh, catalog,
    connections: google.connections || [],
    connected: (google.connections || []).some((c) => c.status === 'active'),
    allLocations, locations, location: location_, single, chooseLocation,
    /** Query value for APIs that take ?location= ("all" or an id). */
    locationParam: location_ ? location_.id : 'all',
    settings: boot?.settings || null,
    demo: Boolean(google.demo), configured: Boolean(google.configured), ai: Boolean(google.ai), places: Boolean(google.places), realtime: Boolean(google.realtime),
    counts: boot?.counts || {},
    range, setRange, link,
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export const useWorkspace = () => useContext(WorkspaceContext);

/** Range query parameters for insight endpoints. */
export const rangeParams = (range) => (range?.preset === 'custom' ? { range: 'custom', since: range.since, until: range.until } : { range: range?.preset || 'last_28d' });

/**
 * Load data and keep loading / error state. `key` decides when to reload;
 * `reload()` refetches without clearing what is on screen.
 */
export function useLoad(load, key, { enabled = true } = {}) {
  const [state, setState] = useState({ data: null, error: null, loading: Boolean(enabled) });
  const latest = useRef(0);
  const run = useCallback(async (quiet = false) => {
    if (!enabled) return;
    const ticket = ++latest.current;
    if (!quiet) setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await load();
      if (ticket === latest.current) setState({ data, error: null, loading: false });
    } catch (error) {
      if (ticket === latest.current) setState((s) => ({ data: quiet ? s.data : null, error: errorText(error), loading: false }));
    }
  }, [enabled, key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, [run]);

  return { ...state, reload: () => run(true) };
}
